import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CContainer, CSpinner } from "@coreui/react-pro";
import PublicContentPageShell from "../components/public/PublicContentPageShell";
import config from "../config/config";
import "../styles/public-experience.css";
import "../styles/public-news.css";

const FALLBACK_CATEGORIES = ["Alle", "Welt", "Europa", "Deutschland", "Frankreich", "Österreich"];

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_error) {
    return "";
  }
}

function excerptLines(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 12)
    .join("\n");
}

function renderExcerpt(value) {
  return excerptLines(value)
    .split(/\r?\n/)
    .map((line, index) => {
      const urlMatch = line.match(/https?:\/\/[^\s)]+/i);
      if (urlMatch) {
        const url = urlMatch[0];
        return (
          <span key={`excerpt-link-${index}`} className="public-news-card__source-link-row">
            <a href={url} target="_blank" rel="noreferrer" className="public-news-card__source-link">
              Originalquelle öffnen
            </a>
          </span>
        );
      }

      return (
        <span key={`excerpt-line-${index}`} className="public-news-card__excerpt-line">
          {line}
        </span>
      );
    });
}

export default function Nachrichten() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "Alle");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Nachrichten | Experts4Europe";
  }, []);

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    async function loadNews() {
      setLoading(true);
      setError("");
      try {
        const url = new URL(`${config.API_BASE_URL}/api/news/public`);
        if (category && category !== "Alle") url.searchParams.set("category", category);
        if (query.trim()) url.searchParams.set("q", query.trim());

        const response = await fetch(url.toString(), {
          signal: controller.signal,
          credentials: "include",
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || "Nachrichten konnten nicht geladen werden.");
        }
        if (ignore) return;
        setItems(Array.isArray(payload?.items) ? payload.items : []);
        setCategories(Array.isArray(payload?.categories) && payload.categories.length ? payload.categories : FALLBACK_CATEGORIES);
      } catch (loadError) {
        if (ignore || loadError.name === "AbortError") return;
        setError(loadError.message || "Nachrichten konnten nicht geladen werden.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    loadNews();
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [category, query]);

  useEffect(() => {
    const next = {};
    if (query.trim()) next.q = query.trim();
    if (category && category !== "Alle") next.category = category;
    setSearchParams(next, { replace: true });
  }, [query, category, setSearchParams]);

  const hero = (
    <section className="public-news-hero">
      <CContainer fluid className="public-core-container public-page-shell public-news-shell public-news-shell--hero public-news-shell--stage">
        <div className="public-news-stage public-news-stage--hero">
          <div className="public-news-hero__panel public-page-card">
          <div className="public-news-hero__copy">
            <span className="public-page-overline">Nachrichten</span>
            <h1 className="public-page-title">Aktuelle Einordnung zu Migration, Fachkräften und Europa.</h1>
            <p className="public-page-body public-news-hero__lead">
              Die Nachrichtenfläche von Experts4Europe bündelt relevante Entwicklungen aus Europa, Deutschland, Frankreich, Österreich und dem internationalen Umfeld. Sie ordnet Themen ein, statt sie nur aneinanderzureihen.
            </p>
          </div>

          <aside className="public-news-hero__meta">
            <article className="public-page-callout public-news-hero__meta-card">
              <strong>Öffentliche Orientierung</strong>
              <span>Alle Beiträge lassen sich nach Suchwort und Region filtern.</span>
            </article>
            <article className="public-page-callout public-news-hero__meta-card">
              <strong>Redaktionell kuratiert</strong>
              <span>Quellen, Kategorien und Sichtbarkeit werden nachvollziehbar geführt.</span>
            </article>
          </aside>
        </div>
        </div>
      </CContainer>
    </section>
  );

  const articleCountLabel = useMemo(() => {
    if (loading) return "Lädt…";
    if (!items.length) return "Keine Beiträge gefunden";
    return `${items.length} Beitrag${items.length === 1 ? "" : "e"}`;
  }, [items.length, loading]);

  const filterBar = (
    <div className="public-news-filterbar public-page-card">
      <div className="public-news-filterbar__copy">
        <span className="public-page-overline">Filter</span>
        <h2 className="public-page-section-title">Nachrichten nach Thema und Region durchsuchen.</h2>
        <p className="public-page-body">{articleCountLabel}</p>
      </div>

      <div className="public-news-filterbar__controls">
        <label className="public-news-filterbar__search" htmlFor="news-search">
          <span>Suchwort</span>
          <input
            id="news-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Begriff eingeben"
          />
        </label>

        <div className="public-news-filterbar__chips" role="tablist" aria-label="Nachrichten-Kategorien">
          {categories.map((entry) => (
            <button
              key={entry}
              type="button"
              className={entry === category ? "is-active" : ""}
              onClick={() => setCategory(entry)}
              aria-pressed={entry === category}
            >
              {entry}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <PublicContentPageShell pageClassName="public-news-page" hero={hero}>
      <section className="public-core-band public-core-band--hero public-news-band public-news-band--filter">
        <CContainer fluid className="public-core-container public-page-shell public-news-shell public-news-shell--filter public-news-shell--stage">
          <div className="public-news-stage public-news-stage--filter">
            {filterBar}
          </div>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--light">
        <CContainer fluid className="public-core-container public-page-shell public-news-shell public-news-shell--content">
          {loading ? (
            <div className="public-news-state public-page-card">
              <CSpinner size="sm" />
              <span>Nachrichten werden geladen…</span>
            </div>
          ) : null}

          {error ? (
            <div className="public-news-state public-news-state--error public-page-card">
              <strong>Hinweis</strong>
              <span>{error}</span>
            </div>
          ) : null}

          {!loading && !error ? (
            <div className="public-news-list">
              {items.map((article) => (
                <article key={article.id || article.slug} className="public-news-card public-page-card">
                  <div className="public-news-card__meta">
                    <span>{article.category || "Alle"}</span>
                    {article.sourceName ? <span>{article.sourceName}</span> : null}
                    {article.viewCount || article.viewCount === 0 ? <span>{article.viewCount} Aufrufe</span> : null}
                    {article.publishedAt ? <span>{formatDate(article.publishedAt)}</span> : null}
                  </div>

                  <div className="public-news-card__content">
                    <h3>
                      <Link to={`/nachrichten/${article.slug}`}>{article.title}</Link>
                    </h3>
                    <div className="public-news-card__excerpt">{renderExcerpt(article.body || article.excerpt)}</div>
                  </div>

                  <div className="public-news-card__footer">
                    <Link to={`/nachrichten/${article.slug}`} className="public-news-card__more">
                      &lt;weiter&gt;
                    </Link>
                  </div>
                </article>
              ))}

              {!items.length ? (
                <div className="public-news-state public-page-card">
                  <strong>Keine Treffer</strong>
                  <span>Für diese Auswahl wurden aktuell keine veröffentlichten Beiträge gefunden.</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </CContainer>
      </section>
    </PublicContentPageShell>
  );
}
